<script setup>
import { ref, provide, onMounted, computed } from 'vue';
import { router } from '@inertiajs/vue3';
import { useOffersStore } from '@/stores/offers';
import { useOptionsStore } from '@/stores/options';
import { useUserStore } from '@/stores/user';
import { storeToRefs } from 'pinia';

const offersStore = useOffersStore();
const { allOfferRequests } = storeToRefs(offersStore);
const userStore = useUserStore();
const { profile } = storeToRefs(userStore);
const params = ref({
    term: '',
    global_status_id: null,
    page: 1,
    per_page: 10,
});

// Options store for dynamic statuses
const optionsStore = useOptionsStore();
const statusOptions = computed(() => optionsStore.statusOptions);

// Determine admin/super admin for conditional columns
const isAdmin = computed(() => {
    const roles = profile.value?.roles || [];
    return roles.some(r => ['admin', 'super_admin'].includes(r.name));
});

const loading = ref(false);
provide('isLoading', loading);
const searchOffers = () => {
    loading.value = true;
    offersStore.search({ ...params.value, is_leads_page: true })
        .then(() => {
            loading.value = false;
        });
};

const clearSearch = () => {
    params.value.term = '';
    searchOffers();
};

const tableUpdate = ({ page, itemsPerPage, sortBy }) => {
    Object.assign(params.value, { page, per_page: itemsPerPage });
    if (sortBy) {
        params.value.sortBy = sortBy; // map of key => order
    }
    searchOffers();
};

onMounted(() => {
    if (!allOfferRequests.value?.data?.length) {
        searchOffers();
    }
});

const createNewOffer = () => {
    router.visit('/offers/new');
};

</script>

<template>
<MainContentLayout>
    <template #breadcrumb><BreadCrumb /></template>
    <template #title>{{ $t('offer.leads_management') }}</template>
    
    <div class="grid lg:grid-cols-3 md:gap-4 md:mb-4">
        <StyledSearch
            v-model="params.term"
            v-debounce:500ms="searchOffers"
            name="search"
            :label="$t('label.search')"
            :debounce-events="['input']"
            @click:clear="clearSearch" />
        <StyledDropdown
            v-model="params.global_status_id"
            clearable
            :placeholder="$t('label.status')"
            :options="statusOptions"
            name="global_status_id"
            @input="searchOffers" />
        <div class="flex justify-end mb-4">
            <StyledButton :loading="creating" class="custom-tab-btn" @click="createNewOffer">{{ $t('offer.create_new_lead') || 'Create New Lead' }}</StyledButton>
        </div>
    </div>
    <DataTable
        :data="allOfferRequests || { data: [] }"
        :headers="[
            ...(isAdmin ? [{ title: $t('offer.agent') || 'Agent', key: 'agent', value: item => {
                const fn = item.agent_first_name || ''; const ln = item.agent_last_name || ''; const full = `${fn} ${ln}`.trim();
                return full || '-';
            }, sortable: false }] : []),
            { title: $t('offer.address'), key: 'address', value: item => item.address, sortable: false },
            { title: $t('offer.seller_valuation_high'), key: 'seller_valuation_high', value: item => item?.valuations?.[0]?.max_value ? `$${Number(item.valuations[0].max_value).toLocaleString()}` : null, sortable: false },
            { title: $t('offer.seller_valuation_low'), key: 'seller_valuation_low', value: item => item?.valuations?.[0]?.min_value ? `$${Number(item.valuations[0].min_value).toLocaleString()}` : null, sortable: false },
            ...(isAdmin ? [{ title: $t('offer.api_value'), key: 'api_value', value: item => item?.valuations?.[0]?.houski_sale_value ? `$${Number(item.valuations[0].houski_sale_value).toLocaleString()}` : null, sortable: false }] : []),
            { 
                title: $t('offer.lead_status') || 'Status',
                key: 'status',
                value: item => item.global_status?.name || '-',
                sortable: true
            }
        ]"
        :actions="item => [
            { title: $t('label.view_lead_submission'), icon: 'mdi-note-edit-outline', action: () => $inertia.visit(`/offers/${item.hash}`) },
        ]"
        @table-update="tableUpdate" />
</MainContentLayout>
</template>

<style scoped>
    .custom-tab-btn {
        background: #00594C !important;
    }
</style>
